import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { TrendingUp, Target, Search, Users, Award, Zap } from "lucide-react";

const VideoMarketingBenefits = () => {
  const anim = useScrollAnimation();

  const benefits = [
    {
      icon: TrendingUp,
      title: "Höhere Conversion-Raten",
      stat: "87%",
      description: "Landing Pages mit Videos erzielen deutlich höhere Conversion-Raten",
      impact: "Mehr Leads und Verkäufe durch professionelle Produktvideos"
    },
    {
      icon: Target,
      title: "Besseres Engagement",
      stat: "5x",
      description: "Video-Content erzielt fünfmal mehr Engagement als statische Posts",
      impact: "Mehr Likes, Kommentare, Shares und Reichweite auf Social Media"
    },
    {
      icon: Search,
      title: "Video-SEO Vorteile",
      stat: "50x",
      description: "Videos haben eine höhere Chance, auf Seite 1 von Google zu ranken",
      impact: "Bessere Sichtbarkeit in Suchmaschinen für Ihr Unternehmen"
    },
    {
      icon: Users,
      title: "Stärkere Kundenbindung",
      stat: "95%",
      description: "Menschen behalten Informationen aus Videos deutlich besser",
      impact: "Ihre Botschaft bleibt im Gedächtnis Ihrer Zielgruppe"
    },
    {
      icon: Award,
      title: "Mehr Vertrauen",
      stat: "89%",
      description: "Kunden fühlen sich durch Videos besser informiert",
      impact: "Imagefilme und Testimonials schaffen Vertrauen und Glaubwürdigkeit"
    },
    {
      icon: Zap,
      title: "Mobile-First Strategie",
      stat: "75%",
      description: "Der Großteil der Videos wird auf mobilen Geräten angesehen",
      impact: "Erreichen Sie Ihre Zielgruppe dort, wo sie aktiv ist"
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-background to-muted/20">
      <div
        ref={anim.ref}
        className={`container-custom transition-all duration-1000 ${
          anim.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Video Marketing: Warum jedes Unternehmen Videos braucht
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            In der digitalen Welt von heute ist Video-Content kein Nice-to-have mehr – es ist ein Muss für erfolgreiches Marketing
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group bg-background/50 backdrop-blur-xs border border-primary/20 rounded-2xl p-6 hover:border-primary/40 hover:shadow-xl transition-all"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="text-3xl font-bold text-primary mb-1">{benefit.stat}</div>
                  <h3 className="text-lg font-semibold">{benefit.title}</h3>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-3">
                {benefit.description}
              </p>
              
              <div className="pt-3 border-t border-primary/20">
                <p className="text-sm font-medium">
                  {benefit.impact}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-8">
          <h3 className="text-2xl font-bold mb-6 text-center">
            Video-Content für jede Phase der Customer Journey
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-background/50 rounded-xl p-6">
              <h4 className="font-semibold mb-3">Awareness (Bekanntheit)</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Social Media Reels & Stories</li>
                <li>• Behind-the-Scenes Content</li>
                <li>• Educational Videos</li>
                <li>• Drohnenaufnahmen</li>
              </ul>
            </div>
            
            <div className="bg-background/50 rounded-xl p-6">
              <h4 className="font-semibold mb-3">Consideration (Überlegung)</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Imagefilme & Unternehmensvideos</li>
                <li>• Produktpräsentationen</li>
                <li>• Customer Testimonials</li>
                <li>• How-to Videos</li>
              </ul>
            </div>
            
            <div className="bg-background/50 rounded-xl p-6">
              <h4 className="font-semibold mb-3">Decision (Entscheidung)</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Werbefilme & Kampagnen</li>
                <li>• Detaillierte Produktvideos</li>
                <li>• Case Studies</li>
                <li>• Experteninterviews</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-foreground max-w-3xl mx-auto">
            <strong>Die Investition lohnt sich:</strong> Unternehmen in Mannheim, Heidelberg, Stuttgart und der gesamten Rhein-Neckar-Region, die auf professionellen Video-Content setzen, berichten von durchschnittlich 66% mehr qualifizierten Leads pro Jahr.
          </p>
          <p className="text-xs text-muted-foreground mt-4">
            Statistiken basierend auf allgemeinen Branchenstudien und Marketing-Research
          </p>
        </div>
      </div>
    </section>
  );
};

export default VideoMarketingBenefits;
